import React from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@ncoder/ui';
import { DatasetsList } from '../../molecules/training/DatasetsList';
import { QualityAnalysis } from '../../molecules/training/QualityAnalysis';
import { TrainingProgress } from '../../molecules/training/TrainingProgress';
import { ValidationResults } from '../../molecules/training/ValidationResults';
import { TrainingStatusPanel } from '../../molecules/training/TrainingStatusPanel';
import { PerformanceMetrics } from '../../molecules/training/PerformanceMetrics';
import { QuickActionsPanel } from '../../molecules/training/QuickActionsPanel';

interface Dataset {
  id: number;
  name: string;
  size: string;
  records: number;
  status: 'processed' | 'processing' | 'pending';
  quality: number;
}

interface TrainingDataContentProps {
  // Datasets
  datasets: Dataset[];
  onViewDataset?: (id: number) => void;
  onEditDataset?: (id: number) => void;
  
  // Quality
  overallQuality: number;
  totalRecords: number;
  
  // Training
  trainingProgress: number;
  epochsCompleted: number;
  totalEpochs: number;
  timeRemaining: string;
  
  // Validation
  accuracy: string;
  precision: string;
  recall: string;
  onRunValidation?: () => void;
  
  // Status
  currentModel: string;
  lastTrained: string;
  trainingTime: string;
  
  // Performance
  responseQuality: string;
  contextUnderstanding: string;
  taskCompletion: string;
  
  // Quick actions
  onViewAnalytics?: () => void;
  onImportDataset?: () => void;
  onStartTraining?: () => void;
}

export const TrainingDataContent: React.FC<TrainingDataContentProps> = ({
  datasets,
  onViewDataset,
  onEditDataset,
  overallQuality,
  totalRecords,
  trainingProgress,
  epochsCompleted,
  totalEpochs,
  timeRemaining,
  accuracy,
  precision,
  recall,
  onRunValidation,
  currentModel,
  lastTrained,
  trainingTime,
  responseQuality,
  contextUnderstanding,
  taskCompletion,
  onViewAnalytics,
  onImportDataset,
  onStartTraining,
}) => {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Main Content - 2/3 width */}
      <div className="lg:col-span-2 space-y-6">
        <Tabs defaultValue="datasets" className="w-full">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="datasets">Datasets</TabsTrigger>
            <TabsTrigger value="quality">Quality</TabsTrigger>
            <TabsTrigger value="training">Training</TabsTrigger>
            <TabsTrigger value="validation">Validation</TabsTrigger>
          </TabsList>

          <TabsContent value="datasets" className="space-y-4">
            <DatasetsList
              datasets={datasets}
              onViewDataset={onViewDataset}
              onEditDataset={onEditDataset}
            />
          </TabsContent>

          <TabsContent value="quality" className="space-y-4">
            <QualityAnalysis
              overallQuality={overallQuality}
              totalRecords={totalRecords}
            />
          </TabsContent>

          <TabsContent value="training" className="space-y-4">
            <TrainingProgress
              progress={trainingProgress}
              epochsCompleted={epochsCompleted}
              totalEpochs={totalEpochs}
              timeRemaining={timeRemaining}
            />
          </TabsContent>

          <TabsContent value="validation" className="space-y-4">
            <ValidationResults
              accuracy={accuracy}
              precision={precision}
              recall={recall}
              onRunValidation={onRunValidation}
            />
          </TabsContent>
        </Tabs>
      </div>

      {/* Sidebar - 1/3 width */}
      <div className="space-y-6">
        <TrainingStatusPanel
          currentModel={currentModel}
          lastTrained={lastTrained}
          trainingTime={trainingTime}
        />

        <PerformanceMetrics
          responseQuality={responseQuality}
          contextUnderstanding={contextUnderstanding}
          taskCompletion={taskCompletion}
        />

        <QuickActionsPanel
          onViewAnalytics={onViewAnalytics}
          onImportDataset={onImportDataset}
          onStartTraining={onStartTraining}
        />
      </div>
    </div>
  );
};
